/**
 * Where the phone shell lives in the URL. A trail's base route is
 * `/trail/:code`; the existing modal routes hang one level under it
 * (`/trail/:code/scan`, `/trail/:code/trail-point/:id` …). The phone's own
 * screens sit under `/trail/:code/app/…` so that:
 *  - the phone's back button walks thread → thread list → home, like a phone;
 *  - none of them collide with a modal route, now or later.
 */

export type PhoneScreen =
  | { screen: 'base' }
  | { screen: 'map' }
  | { screen: 'messages' }
  | { screen: 'thread'; threadKey: string }
  | { screen: 'other' };

function segments(pathname: string): string[] {
  return pathname.replace(/\/+$/, '').split('/');
}

/**
 * True when nothing is open over the trail: the base route itself or one of
 * the phone's screens. Trail.tsx used to ask "are there exactly three path
 * segments?", which the phone's deeper routes would fail.
 */
export function isTrailView(pathname: string): boolean {
  const parts = segments(pathname);
  return parts.length <= 3 || parts[3] === 'app';
}

export function phoneScreenFor(pathname: string): PhoneScreen {
  const parts = segments(pathname);
  if (parts.length <= 3) return { screen: 'base' };
  if (parts[3] !== 'app') return { screen: 'other' };
  if (parts[4] === 'map') return { screen: 'map' };
  if (parts[4] === 'messages') {
    return parts[5]
      ? { screen: 'thread', threadKey: decodeURIComponent(parts[5]) }
      : { screen: 'messages' };
  }
  return { screen: 'other' };
}

/** `/trail/:code` out of any path under it. */
export function trailBaseUrl(pathname: string): string {
  return segments(pathname).slice(0, 3).join('/');
}

export const phonePaths = {
  map: (base: string) => `${base}/app/map`,
  messages: (base: string) => `${base}/app/messages`,
  thread: (base: string, threadKey: string) =>
    `${base}/app/messages/${encodeURIComponent(threadKey)}`,
  scan: (base: string) => `${base}/scan`,
  codeEntry: (base: string) => `${base}/enter-code`,
  messagePage: (base: string, pointCode: string, pageIndex: number) =>
    `${base}/message-page/${encodeURIComponent(pointCode)}/${pageIndex}`,
  trailPoint: (base: string, pointCode: string) =>
    `${base}/trail-point/${encodeURIComponent(pointCode)}`,
};
